import { useState } from 'react';
import { motion } from 'motion/react';
import { Check, Smartphone, Globe, Sparkles, ArrowRight, Zap, Crown, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const features = [
  { text: 'Estudiantes ilimitados', highlight: true },
  { text: 'Tareas y materias ilimitadas' },
  { text: 'Calendario completo + recordatorios' },
  { text: 'Escaneo de tareas con IA', highlight: true },
  { text: 'Ayuda con tareas con IA', highlight: true },
  { text: 'Planes de estudio personalizados', highlight: true },
  { text: 'Acceso web incluido' },
  { text: 'Sin anuncios' },
  { text: 'Soporte prioritario' },
];

export function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);

  const price = isAnnual ? '$2.08' : '$2.99';
  const period = '/mes';

  return (
    <section id="pricing" className="py-24 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background" />
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.3, 0.2] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-1/4 -left-32 w-96 h-96 bg-gradient-to-br from-[#a67eec]/15 to-[#5bb4f2]/15 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-gradient-to-br from-violet-500/15 to-pink-500/15 rounded-full blur-3xl"
      />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-10"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary/20 text-primary text-sm font-semibold mb-6 shadow-sm"
          >
            Planes y Precios
          </motion.span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 tracking-tight">
            Precios justos para{' '}
            <span className="bg-gradient-to-r from-[#a67eec] via-[#5bb4f2] to-[#5ddba3] bg-clip-text text-transparent">
              Latinoamérica
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Prueba todo gratis por 7 días. Sin tarjeta requerida.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-4 mb-14"
        >
          <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
            Mensual
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="relative w-14 h-7 rounded-full bg-gradient-to-r from-[#a67eec] to-[#8b6de0] p-0.5 transition-all shadow-lg shadow-primary/20"
          >
            <motion.div
              animate={{ x: isAnnual ? 28 : 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="w-6 h-6 rounded-full bg-white shadow-md"
            />
          </button>
          <span className={`text-sm font-medium transition-colors flex items-center gap-2 ${isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
            Anual
            <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 text-white text-xs font-bold shadow-sm">
              -30%
            </span>
          </span>
        </motion.div>

        {/* Single Premium Card */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-lg mx-auto"
        >
          <div className="relative p-8 lg:p-10 rounded-3xl bg-white/90 backdrop-blur-sm border border-primary/25 shadow-2xl shadow-primary/10">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="px-5 py-2 rounded-full bg-gradient-to-r from-[#a67eec] to-[#8b6de0] text-white text-sm font-semibold flex items-center gap-1.5 shadow-xl shadow-primary/30">
                <Crown className="w-4 h-4" />
                7 días gratis
              </span>
            </div>

            {/* Header */}
            <div className="text-center mb-8 pt-2">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#a67eec] to-[#8b6de0] mb-4 shadow-xl"
              >
                <Zap className="w-8 h-8 text-white" />
              </motion.div>
              <h3 className="text-2xl font-bold text-foreground">Premium</h3>
              <p className="text-muted-foreground mt-1">Todo incluido para toda la familia</p>
            </div>

            {/* Price */}
            <div className="text-center mb-8">
              <motion.div
                key={price}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-baseline justify-center gap-1"
              >
                <span className="text-5xl lg:text-6xl font-extrabold bg-gradient-to-r from-[#a67eec] to-[#8b6de0] bg-clip-text text-transparent">
                  {price}
                </span>
                <span className="text-muted-foreground text-lg">{period}</span>
              </motion.div>

              {isAnnual ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-2 flex items-center justify-center gap-2"
                >
                  <span className="text-sm text-muted-foreground">$24.99/año total</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                    Ahorra 30%
                  </span>
                </motion.div>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  o $24.99/año{' '}
                  <button onClick={() => setIsAnnual(true)} className="text-primary font-semibold hover:underline">
                    (ahorra 30%)
                  </button>
                </p>
              )}
            </div>

            {/* Trial highlight */}
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-md">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Prueba 7 días gratis</p>
                <p className="text-xs text-gray-500">Sin tarjeta al inicio. Cancela cuando quieras.</p>
              </div>
            </div>

            {/* Features */}
            <ul className="space-y-3.5 mb-8">
              {features.map((feature) => (
                <li key={feature.text} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#a67eec] to-[#8b6de0] flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className={`text-sm ${feature.highlight ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {feature.text}
                    {feature.highlight && (
                      <Sparkles className="inline-block w-3.5 h-3.5 ml-1 text-amber-500" />
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button className="w-full h-13 text-base font-semibold bg-gradient-to-r from-[#a67eec] to-[#8b6de0] hover:from-[#9568d9] hover:to-[#7d5cc8] text-white shadow-xl shadow-primary/25 group" size="lg">
                Comenzar prueba gratis
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>

            <p className="text-xs text-center text-muted-foreground mt-3">
              Sin compromiso · Cancela cuando quieras
            </p>
          </div>
        </motion.div>

        {/* Value Badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mt-12"
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-4 px-6 py-4 rounded-2xl bg-white/60 backdrop-blur-sm border border-primary/15 shadow-lg shadow-primary/5">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center shadow-md">
                <Smartphone className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-foreground">App móvil</span>
            </div>
            <span className="text-2xl font-bold text-primary">+</span>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center shadow-md">
                <Globe className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-foreground">Acceso web</span>
            </div>
            <span className="text-2xl text-muted-foreground">=</span>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-md">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold bg-gradient-to-r from-[#a67eec] to-[#8b6de0] bg-clip-text text-transparent text-lg">
                1 sola suscripción
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bottom info */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="max-w-2xl mx-auto text-center mt-10"
        >
          <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
            {['Precios en USD', 'Pago seguro via Google Play', 'Precio local en tu moneda'].map((text) => (
              <div key={text} className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-600" />
                </div>
                {text}
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Disponible en{' '}
            <span className="font-medium text-foreground">México, Colombia, Argentina, Brasil, Perú, Chile</span>
            {' '}y más países.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
